import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import TextField from "@mui/material/TextField";
import AutocompleteMUI from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

function Autocomplete({
  methods,
  label,
  name,
  required,
  variant,
  noOptionsText,
  renderOption,
  onChange,
  dataLoader,
  isOptionEqualToValue,
  getOptionLabel,
  loadingText,
}) {
  const {
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = methods;
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const value = getValues(name);

  useEffect(() => {
    dataLoader(inputValue, value, setOptions, setLoading);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, value]);

  return (
    <React.Fragment>
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        onChange={([, data]) => data}
        render={({ field }) => (
          <AutocompleteMUI
            {...field}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            filterOptions={(x) => x}
            options={options}
            autoComplete
            includeInputInList
            noOptionsText={noOptionsText}
            value={field.value || null}
            onChange={(event, newValue) => {
              setValue(name, newValue);
              trigger(name);
              onChange(event, newValue);
            }}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
            }}
            loading={loading}
            loadingText={loadingText}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                fullWidth
                required={!!required}
                error={errors?.hasOwnProperty(name)}
                helperText={
                  errors?.hasOwnProperty(name) && errors[name].message
                    ? errors[name].message
                    : " "
                }
                variant={variant}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            renderOption={renderOption}
          />
        )}
      />
    </React.Fragment>
  );
}

Autocomplete.defaultProps = {
  label: "Autocomplete",
  name: "autocompletion",
  required: "Merci de saisir ...",
  variant: "outlined",
  noOptionsText: "Pas de résultat",
  renderOption: (props, option) => <li {...props}>{option.label}</li>,
  onChange: (event, newValue) => {
    console.log(event, newValue);
  },
  dataLoader: (inputValue, value, setOptions, setLoading) => {
    setLoading(true);
    console.log(inputValue, value);
    setOptions([{ id: 1, label: "test" }]);
    setLoading(false);
  },
  isOptionEqualToValue: (option, value) => option.id === value.id,
  getOptionLabel: (option) =>
    typeof option === "string" ? option : option.label,
  loadingText: "Chargement...",
  defaultValue: "",
};

Autocomplete.propTypes = {
  /**
   * methods du useForm()
   */
  methods: PropTypes.instanceOf(Object),
  /**
   * Label du champ
   */
  label: PropTypes.string,
  /**
   * Name du tag HTML input
   */
  name: PropTypes.string,
  /**
   * Message d'erreur si le champ est requis
   */
  required: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * The variant to use
   * 'filled'
   * | 'outlined'
   * | 'standard'
   */
  variant: PropTypes.string,
  /**
   * Texte si pas de résultat
   */
  noOptionsText: PropTypes.string,
  /**
   * Fonction à appeler lors du changement de valeur
   * onChange: (event, newValue) => {}
   */
  onChange: PropTypes.func,
  /**
   * Fonction à appeler pour charger les données en asynchrone
   * dataLoader: (inputValue, value, setOptions) => {}
   */
  dataLoader: PropTypes.func,
  /**
   * Fonction appelée pour comparer identifier la valeur sélectionnée
   * isOptionEqualToValue: (option, value) => option.id === value.id
   */
  isOptionEqualToValue: PropTypes.func,
  /**
   * Fonction appelée pour choisir le label à afficher
   * getOptionLabel: (option) => typeof option === "string" ? option : option.label,
   */
  getOptionLabel: PropTypes.func,
  /**
   * Texte à afficher lors du chargement des résultats
   */
  loadingText: PropTypes.string,
};

export default Autocomplete;
